import styled from 'styled-components';
import { useCart } from 'context/CartContext';
import { HideTablet, StyledRow } from 'components/styles/CommonStyles';
import { useModal } from 'context/ModalContext';

import {
    Caption,
    Caption2,
    Subtitle3,
    Text,
} from 'components/styles/Typography';
import { useRouter } from 'next/router';
import ButtonNew from '@components/ui/Button';
import useTranslation from '@hooks/useTranslation';
import { sizes } from 'styles/variables';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CartServices } from '@services';
import { addToBasketEvent } from '@lib/gtm';
import { useState } from 'react';
import SizeForm from './SizeForm';

const SizePicker = ({
    product,
    setShowWarning,
    isSizePickerOpen,
    setShowError,
    showError,
    setVariantQuantity,
    variantQuantity,
    selectedSize,
    mobileBuyMenu,
}) => {
    const { t } = useTranslation();
    const { basketHash, currentUser } = useCart();
    const { openCartModal } = useModal();
    const queryClient = useQueryClient();
    const router = useRouter();
    const isProdRequest = !!router.query['production-request'];
    const [isButtonClicked, setIsButtonClicked] = useState(false);

    const addToCartMutation = useMutation({
        mutationFn: CartServices.addToCart,
        onSuccess: (data, { products: addedProducts }) => {
            // queryClient.setQueryData(['getCart'], data);
            queryClient.invalidateQueries(['getCart']);

            openCartModal();
            addToBasketEvent({ products: data.data.products, addedProducts });
            setVariantQuantity([]);
            setIsButtonClicked(false);
        },
    });
    const handleAddToCart = () => {
        setIsButtonClicked(true);

        if (variantQuantity.length > 0) {
            addToCartMutation.mutate({
                basketHash,
                products: variantQuantity,
                token: currentUser,
            });
        }
    };
    const showPickSizeText = !selectedSize || selectedSize === '';
    const buttonText =
        isButtonClicked && showPickSizeText
            ? 'Enter Pack Number'
            : t('addToBasket');
    if (showError) {
        setTimeout(() => {
            setShowError(false);
        }, 5000);
    }
    const renderReminderText = () => {
        if (isProdRequest) {
            return 'Stock Availabity : Min 10 Packs ';
        }
        if (showError) {
            return showError;
        }

        return '1 PACK = 3 UNITS';
    };
    const variantQuantityCount = variantQuantity?.reduce(
        (acc, item) => acc + item.quantity,
        0,
    );

    const totalPrice = variantQuantity?.reduce((acc, item) => {
        const variant = product.variants.find(
            (variant) => variant.id === item.productId,
        );

        return acc + variant.price.USD * item.quantity * 3;
    }, 0);

    return (
        <PickerWrapper data-testid="size-picker">
            {isSizePickerOpen && (
                <>
                    <StyledRow justifyContent="unset">
                        <Subtitle3>Total: </Subtitle3>
                        <Caption2>
                            {variantQuantityCount} Pack,{' '}
                            {variantQuantityCount * 3} Units
                        </Caption2>
                    </StyledRow>
                    <PackReminder>
                        <Text
                            size="14px"
                            color={showError ? '#000000' : '#7F7F7F'}
                        >
                            {renderReminderText()}
                        </Text>
                    </PackReminder>
                </>
            )}

            <StyledRow>
                <SizeVariantsWrapper mobileBuyMenu={mobileBuyMenu}>
                    <SizePickerHeader>
                        <Caption
                            style={{ fontSize: sizes.l, lineHeight: 'unset' }}
                        >
                            COLOR
                        </Caption>
                        {isProdRequest && (
                            <Text>
                                You must purchase at least 10 packages for this
                                product to be manufactured and shipped to you.
                                For more, you can increase your order quantity
                                in triplicate.
                            </Text>
                        )}
                    </SizePickerHeader>
                    {product?.variants
                        ?.filter((variant) => variant.id === product.id)
                        .map((variant) => {
                            if (isProdRequest) {
                                return (
                                    <SizeForm
                                        key={variant.id}
                                        variant={variant}
                                        setVariantQuantity={setVariantQuantity}
                                        isProdRequest={isProdRequest}
                                        setShowWarning={setShowWarning}
                                        setShowError={setShowError}
                                        variantQuantity={variantQuantity}
                                        isButtonClicked={isButtonClicked}
                                    />
                                );
                            }
                            if (variant?.pack_count > 0) {
                                return (
                                    <SizeForm
                                        key={variant.id}
                                        variant={variant}
                                        setVariantQuantity={setVariantQuantity}
                                        isProdRequest={isProdRequest}
                                        setShowWarning={setShowWarning}
                                        setShowError={setShowError}
                                        variantQuantity={variantQuantity}
                                        isButtonClicked={isButtonClicked}
                                    />
                                );
                            }
                            return null;
                        })}
                </SizeVariantsWrapper>
            </StyledRow>
            <HideTablet>
                <ButtonWrapper>
                    <TextWrapper>
                        <Quantity>
                            <Text style={{ margin: '5px' }}>
                                Total Packs: {variantQuantityCount}
                            </Text>
                            <Text style={{ margin: '5px' }}>
                                Total Units: {variantQuantityCount * 3}
                            </Text>
                        </Quantity>
                        <Text style={{ margin: '5px' }}>
                            Subtotal: {totalPrice}$
                        </Text>
                    </TextWrapper>
                    <ButtonNew
                        content={buttonText}
                        size="xlarge"
                        type="primary"
                        onClick={handleAddToCart}
                        letterSpacing="2px"
                        style={{ width: '100%' }}
                    />
                </ButtonWrapper>
            </HideTablet>
        </PickerWrapper>
    );
};
const PickerWrapper = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;
`;
const SizeVariantsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    max-height: none;
    overflow-y: visible;
    &::-webkit-scrollbar {
        display: none;
    }
    @media (max-width: 1024px) {
        width: 100%;
        max-height: none;
    }
`;

const SizePickerHeader = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    p {
        margin-bottom: 30px;
        color: #000000;
    }
`;
const ButtonWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: calc(100% - 188px);
    margin-left: auto;
    margin-bottom: 20px;
    &:after {
        content: '';
        position: absolute;
        left: 0;
        width: 100%;
        height: 1px;
        background-color: #cacaca;
    }
    @media (max-width: 1024px) {
        width: 100%;
        margin-bottom: 32px;
    }
`;

const PackReminder = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 8px 0;
    margin: 17px 0;
    background: #f4f4f4;
`;
const TextWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    margin: 5px 0;
`;
const Quantity = styled.div`
    display: flex;
    flex-direction: column;
`;

export default SizePicker;
